import React, { useEffect, useState } from 'react';
import { reportService } from '../services/api';
import { BarChart3, Calendar, DollarSign, FileText, Download, Eye, X } from 'lucide-react';

interface ClientReport {
  id: number;
  report_type: 'facturado' | 'aging' | 'cheques';
  original_name: string;
  date_from: string;
  date_to: string;
  num_facturas: number;
  total_facturado: number;
  total_deducible: number;
  total_cobrado: number;
  total_neto: number;
  plans_data: any;
  created_at: string;
  uploaded_by_first_name: string;
  uploaded_by_last_name: string;
}

const ClientReports: React.FC = () => {
  const [reports, setReports] = useState<ClientReport[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'facturado' | 'aging' | 'cheques'>('facturado');
  const [selectedReport, setSelectedReport] = useState<ClientReport | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [csvData, setCsvData] = useState<string[][] | null>(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [previewType, setPreviewType] = useState<'pdf' | 'csv'>('pdf');

  // Filter reports by type
  const facturadoReports = reports.filter(r => r.report_type === 'facturado' || !r.report_type);
  const agingReports = reports.filter(r => r.report_type === 'aging');
  const chequesReports = reports.filter(r => r.report_type === 'cheques');
  const currentReports = activeTab === 'facturado' ? facturadoReports : activeTab === 'aging' ? agingReports : chequesReports;

  useEffect(() => {
    const fetchReports = async () => {
      try {
        const response = await reportService.getMyReports();
        setReports(response.data);
        // Select first facturado report by default
        const facturado = response.data.filter((r: ClientReport) => r.report_type === 'facturado' || !r.report_type);
        if (facturado.length > 0) {
          setSelectedReport(facturado[0]);
        }
      } catch (error) {
        console.error('Error fetching reports:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchReports();
  }, []);

  // Update selected report when tab changes
  useEffect(() => {
    if (currentReports.length > 0) {
      setSelectedReport(currentReports[0]);
    } else {
      setSelectedReport(null);
    }
  }, [activeTab]);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);
  };

  const formatDate = (dateStr: string) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US');
  };

  const handleDownload = async (report: ClientReport) => {
    try {
      const response = await reportService.download(report.id);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', report.original_name);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading report:', error);
      alert('Error al descargar el reporte');
    }
  };

  const handlePreview = async (report: ClientReport) => {
    setLoadingPreview(true);
    setShowPreview(true);
    
    // Detect file type
    const ext = report.original_name.toLowerCase().split('.').pop();
    const isCSV = ext === 'csv';
    setPreviewType(isCSV ? 'csv' : 'pdf');
    
    try {
      const response = await reportService.download(report.id);
      
      if (isCSV) {
        // Parse CSV
        const text = await response.data.text();
        const rows = text.split('\n').map((row: string) => 
          row.split(',').map((cell: string) => cell.trim().replace(/^"|"$/g, ''))
        ).filter((row: string[]) => row.some(cell => cell));
        setCsvData(rows);
        setPdfUrl(null);
      } else {
        // PDF
        const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
        setPdfUrl(url);
        setCsvData(null);
      }
    } catch (error) {
      console.error('Error loading file:', error);
      alert('Error al cargar el archivo');
      setShowPreview(false);
    } finally {
      setLoadingPreview(false);
    }
  };

  const closePreview = () => {
    setShowPreview(false);
    if (pdfUrl) {
      window.URL.revokeObjectURL(pdfUrl);
      setPdfUrl(null);
    }
    setCsvData(null);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-800">Mis Reportes</h1>
        <p className="text-gray-500">Consulte los reportes subidos por su gestor</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-gray-200">
        <button
          onClick={() => setActiveTab('facturado')}
          className={`px-6 py-3 font-medium transition border-b-2 -mb-px ${
            activeTab === 'facturado'
              ? 'text-blue-600 border-blue-600'
              : 'text-gray-500 border-transparent hover:text-gray-700'
          }`}
        >
          <div className="flex items-center gap-2">
            <DollarSign size={18} />
            Total Facturado
            {facturadoReports.length > 0 && (
              <span className="bg-blue-100 text-blue-600 text-xs px-2 py-0.5 rounded-full">
                {facturadoReports.length}
              </span>
            )}
          </div>
        </button>
        <button
          onClick={() => setActiveTab('aging')}
          className={`px-6 py-3 font-medium transition border-b-2 -mb-px ${
            activeTab === 'aging'
              ? 'text-orange-600 border-orange-600'
              : 'text-gray-500 border-transparent hover:text-gray-700'
          }`}
        >
          <div className="flex items-center gap-2">
            <Calendar size={18} />
            Aging
            {agingReports.length > 0 && (
              <span className="bg-orange-100 text-orange-600 text-xs px-2 py-0.5 rounded-full">
                {agingReports.length}
              </span>
            )}
          </div>
        </button>
        <button
          onClick={() => setActiveTab('cheques')}
          className={`px-6 py-3 font-medium transition border-b-2 -mb-px ${
            activeTab === 'cheques'
              ? 'text-teal-600 border-teal-600'
              : 'text-gray-500 border-transparent hover:text-gray-700'
          }`}
        >
          <div className="flex items-center gap-2">
            <FileText size={18} />
            Cheques Registrados
            {chequesReports.length > 0 && (
              <span className="bg-teal-100 text-teal-600 text-xs px-2 py-0.5 rounded-full">
                {chequesReports.length}
              </span>
            )}
          </div>
        </button>
      </div>

      {currentReports.length === 0 ? (
        <div className="bg-white rounded-xl shadow p-12 text-center">
          <BarChart3 size={64} className="mx-auto mb-4 text-gray-300" />
          <h3 className="text-lg font-medium text-gray-600">
            No hay reportes de {activeTab === 'facturado' ? 'Total Facturado' : activeTab === 'aging' ? 'Aging' : 'Cheques Registrados'}
          </h3>
          <p className="text-gray-500 text-sm mt-1">Sus reportes aparecerán aquí cuando estén disponibles</p>
        </div>
      ) : (
        <div className="space-y-6">
          {/* Report Selector */}
          <div className="bg-white rounded-xl shadow p-4">
            <div className="flex flex-wrap items-center gap-4">
              <label className="font-medium text-gray-700">Seleccionar Reporte:</label>
              <select
                value={selectedReport?.id || ''}
                onChange={(e) => {
                  const report = currentReports.find(r => r.id === parseInt(e.target.value));
                  if (report) setSelectedReport(report);
                }}
                className="flex-1 min-w-[300px] px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                {currentReports.map(report => (
                  <option key={report.id} value={report.id}>
                    {report.original_name} ({formatDate(report.date_from)} - {formatDate(report.date_to)})
                  </option>
                ))}
              </select>
              {selectedReport && (
                <button
                  onClick={() => handleDownload(selectedReport)}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition"
                >
                  <Download size={18} />
                  Descargar
                </button>
              )}
            </div>
          </div>

          {/* Report Details - Full Width */}
          {selectedReport && (
            <div className="bg-white rounded-xl shadow p-6">
              <div className="mb-6">
                <p className="text-sm text-gray-400">
                  Subido el {formatDate(selectedReport.created_at)} por {selectedReport.uploaded_by_first_name} {selectedReport.uploaded_by_last_name}
                </p>
              </div>

              {/* Summary Cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4 mb-6">
                <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-4 text-white">
                  <div className="flex items-center gap-2">
                    <FileText size={20} />
                    <span className="text-sm opacity-90"># Facturas</span>
                  </div>
                  <p className="text-2xl font-bold mt-2">{selectedReport.num_facturas}</p>
                </div>
                <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl p-4 text-white">
                  <div className="flex items-center gap-2">
                    <DollarSign size={20} />
                    <span className="text-sm opacity-90">Facturado</span>
                  </div>
                  <p className="text-2xl font-bold mt-2">{formatCurrency(selectedReport.total_facturado)}</p>
                </div>
                <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl p-4 text-white">
                  <div className="flex items-center gap-2">
                    <DollarSign size={20} />
                    <span className="text-sm opacity-90">Deducible</span>
                  </div>
                  <p className="text-2xl font-bold mt-2">{formatCurrency(selectedReport.total_deducible)}</p>
                </div>
                {selectedReport.total_cobrado > 0 && (
                  <div className="bg-gradient-to-br from-teal-500 to-teal-600 rounded-xl p-4 text-white">
                    <div className="flex items-center gap-2">
                      <DollarSign size={20} />
                      <span className="text-sm opacity-90">Cobrado</span>
                    </div>
                    <p className="text-2xl font-bold mt-2">{formatCurrency(selectedReport.total_cobrado)}</p>
                  </div>
                )}
                <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl p-4 text-white">
                  <div className="flex items-center gap-2">
                    <DollarSign size={20} />
                    <span className="text-sm opacity-90">Neto</span>
                  </div>
                  <p className="text-2xl font-bold mt-2">{formatCurrency(selectedReport.total_neto)}</p>
                </div>
              </div>

              {/* Plans Breakdown */}
              {(() => {
                let plansArray: any[] = [];
                if (selectedReport.plans_data) {
                  if (typeof selectedReport.plans_data === 'string') {
                    try {
                      const parsed = JSON.parse(selectedReport.plans_data);
                      plansArray = Array.isArray(parsed) ? parsed : [];
                    } catch (e) {
                      plansArray = [];
                    }
                  } else if (Array.isArray(selectedReport.plans_data)) {
                    plansArray = selectedReport.plans_data;
                  }
                }
                if (!Array.isArray(plansArray) || plansArray.length === 0) return null;
                return (
                  <div className="border rounded-xl overflow-hidden">
                    <div className="px-4 py-3 bg-gray-50 border-b">
                      <h3 className="font-semibold text-gray-800">Totales por Plan</h3>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Plan</th>
                            <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">#</th>
                            <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Facturado</th>
                            <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Deducible</th>
                            <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Cobrado</th>
                            <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Balance</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                          {plansArray.map((plan: any, index: number) => (
                            <tr key={index} className="hover:bg-gray-50">
                              <td className="px-4 py-3 text-sm font-medium text-gray-800">{plan.planName}</td>
                              <td className="px-4 py-3 text-sm text-right text-gray-600">{plan.numFacturas}</td>
                              <td className="px-4 py-3 text-sm text-right text-blue-600">{formatCurrency(plan.facturado)}</td>
                              <td className="px-4 py-3 text-sm text-right text-orange-600">{formatCurrency(plan.deducible)}</td>
                              <td className="px-4 py-3 text-sm text-right text-gray-600">{formatCurrency(plan.cobrado || 0)}</td>
                              <td className="px-4 py-3 text-sm text-right text-green-600 font-semibold">{formatCurrency(plan.neto)}</td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot className="bg-gray-100">
                          <tr>
                            <td className="px-4 py-3 text-sm font-bold text-gray-800">TOTAL</td>
                            <td className="px-4 py-3 text-sm text-right font-bold">{selectedReport.num_facturas}</td>
                            <td className="px-4 py-3 text-sm text-right font-bold text-blue-700">{formatCurrency(selectedReport.total_facturado)}</td>
                            <td className="px-4 py-3 text-sm text-right font-bold text-orange-700">{formatCurrency(selectedReport.total_deducible)}</td>
                            <td className="px-4 py-3 text-sm text-right font-bold text-gray-700">{formatCurrency(selectedReport.total_cobrado || 0)}</td>
                            <td className="px-4 py-3 text-sm text-right font-bold text-green-700">{formatCurrency(selectedReport.total_neto)}</td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  </div>
                );
              })()}
            </div>
          )}
        </div>
      )}

      {/* File Preview Modal */}
      {showPreview && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-6xl h-[90vh] flex flex-col">
            <div className="p-4 border-b border-gray-200 flex justify-between items-center">
              <h3 className="text-lg font-semibold text-gray-800">
                {selectedReport?.original_name}
              </h3>
              <button
                onClick={closePreview}
                className="p-2 hover:bg-gray-100 rounded-lg transition"
              >
                <X size={24} />
              </button>
            </div>
            <div className="flex-1 p-4 overflow-auto">
              {loadingPreview ? (
                <div className="flex items-center justify-center h-full">
                  <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
                </div>
              ) : previewType === 'csv' && csvData ? (
                <div className="overflow-auto h-full">
                  <table className="w-full text-sm border-collapse">
                    <thead className="bg-gray-100 sticky top-0">
                      <tr>
                        {csvData[0]?.map((header, i) => (
                          <th key={i} className="px-3 py-2 text-left border border-gray-200 font-semibold text-gray-700">
                            {header}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {csvData.slice(1).map((row, rowIdx) => (
                        <tr key={rowIdx} className={rowIdx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                          {row.map((cell, cellIdx) => (
                            <td key={cellIdx} className="px-3 py-2 border border-gray-200 text-gray-600">
                              {cell}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : pdfUrl ? (
                <iframe
                  src={pdfUrl}
                  className="w-full h-full rounded-lg border"
                  title="PDF Preview"
                />
              ) : (
                <div className="flex items-center justify-center h-full text-gray-500">
                  No se pudo cargar el archivo
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientReports;
